import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import {
  ETYKIETA_ZAKRESU, tresciApi, type KolizjaZastosowania, type NoweZastosowanie, type ZakresPaska,
} from '@/lib/api/tresci';
import { useAuth } from '@/lib/auth/AuthContext';
import {
  konfliktyZBledu, oknoWarszawa, opisAdresatow, opisKolizjiZastosowania, zgodnosc, type PozycjaBiblioteki,
} from '@/lib/tresci/biblioteka';
import {
  DOPISEK_WYNIK_NIEZNANY, komunikatBledu, PUSCI_ODBIORCY, wynikNieznany, type WartoscOdbiorcow,
} from '@/lib/tresci/tresciUi';
import { HarmonogramOdDo, type WartoscHarmonogramu } from './HarmonogramOdDo';
import { PotwierdzZamiane } from './PotwierdzZamiane';
import { WyborOdbiorcow } from './WyborOdbiorcow';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ZAPLANUJ SKÓRKĘ — gdzie i kiedy (Skórki 2.0, OWNER 16.10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * A. GDZIE: Wszyscy / Miasto / Szkoła / Konkretne osoby — miasta i szkoły z katalogu systemu głównego,
 *    osoby z wyszukiwarki (fraza w treści POST, wyniki zamaskowane). Wiele adresatów naraz.
 * B. KIEDY: OD/DO w czasie warszawskim (zapis UTC), szybkie opcje, „bez daty końca" tylko świadomie.
 * Priorytet rozstrzyga wyłącznie w tym samym zakresie — węższy zakres wygrywa zawsze (USER > SCHOOL > CITY >
 * GLOBAL), tak samo w aplikacji i w „Sprawdź widok użytkownika" (jeden resolver systemu głównego).
 *
 * Kontrola kolizji idzie na żywo PRZED zapisem: ten sam zakres + wspólni adresaci + nachodzący termin + równy
 * priorytet = konflikt (zapis zablokowany, lista „na co nachodzi"); różny priorytet = informacja, kto wygra.
 * Paczka skórki nie jest kopiowana — zastosowanie wskazuje rewizję z biblioteki.
 *
 * „ZAMIEŃ NA TĘ SKÓRKĘ” (OWNER 17.10): przy konflikcie operator nie musi wychodzić z okna — potwierdza zamianę
 * i system główny w JEDNEJ transakcji wyłącza dokładnie te kolidujące zastosowania i zapisuje nowe. Blokada zapisu
 * zostaje (zwykłe „Potwierdź i zaplanuj” dalej nie przejdzie przy konflikcie).
 */

const OPIS_ZAKRESU: Readonly<Record<ZakresPaska, string>> = {
  GLOBAL: 'Skórkę zobaczą wszyscy użytkownicy — także niezalogowani.',
  CITY: 'Uczniowie szkół w wybranych miastach (lista z systemu głównego). Można wybrać kilka miast.',
  SCHOOL: 'Uczniowie wybranych szkół — najpierw opcjonalnie miasto, potem szkoły. Można wybrać kilka.',
  USER: 'Wskazane osoby z wyszukiwarki — bez wklejania identyfikatorów. Można wybrać kilka.',
};

export interface ZaplanujSkorkeProps {
  p: PozycjaBiblioteki;
  onZamknij: () => void;
  onZaplanowano: (tekst: string) => void;
  onWynikNieznany: () => void;
  /** Zakres na start (np. „+ Dodaj filtr” z zakładki „Szkoły” panelu zastosowań). */
  poczatkowyZakres?: ZakresPaska;
}

export function ZaplanujSkorke({ p, onZamknij, onZaplanowano, onWynikNieznany, poczatkowyZakres = 'GLOBAL' }: ZaplanujSkorkeProps) {
  const { api } = useServices();
  const [zakres, setZakres] = useState<ZakresPaska>(poczatkowyZakres);
  const [odbiorcy, setOdbiorcy] = useState<WartoscOdbiorcow>(PUSCI_ODBIORCY);
  const [konfliktyZapisu, setKonfliktyZapisu] = useState<KolizjaZastosowania[] | null>(null);
  const [h, setH] = useState<WartoscHarmonogramu | null>(null);
  // Stabilna funkcja: harmonogram zgłasza zmianę w efekcie — nowa funkcja w każdym renderze zapętliłaby odświeżanie.
  const onH = useCallback((w: WartoscHarmonogramu) => { setH(w); setKonfliktyZapisu(null); }, []);
  const [priorytet, setPriorytet] = useState('0');
  const [kontrola, setKontrola] = useState<{ klucz: string; konflikty: KolizjaZastosowania[]; nakladania: KolizjaZastosowania[] } | null>(null);
  const [bladKontroli, setBladKontroli] = useState<string | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [trwa, setTrwa] = useState(false);
  const [zamiana, setZamiana] = useState(false);
  const [uwagaZamiany, setUwagaZamiany] = useState<string | null>(null);
  const auth = useAuth();
  const z = zgodnosc(p.minSilnik);
  const rewizja = p.doZastosowania;

  const prio = Number(priorytet);
  const prioOk = /^\d{1,3}$/.test(priorytet) && prio >= 0 && prio <= 100;
  const adresaciOk = zakres === 'GLOBAL'
    || (zakres === 'CITY' && odbiorcy.miasta.length > 0)
    || (zakres === 'SCHOOL' && odbiorcy.szkoly.length > 0)
    || (zakres === 'USER' && odbiorcy.uczniowie.length > 0);
  const startIso = h?.start ? h.start.toISOString() : null;
  const koniecIso = h?.koniec && !h.bezKonca ? h.koniec.toISOString() : null;
  const terminOk = !!h?.ok && !!startIso && (koniecIso !== null || !!h?.bezKonca);

  // Pełne żądanie albo `null` (niekompletne) — z niego liczy się kontrola na żywo i zapis.
  const zadanie: NoweZastosowanie | null = useMemo(() => (rewizja && prioOk && adresaciOk && terminOk ? {
    revisionId: rewizja.id,
    scope: zakres,
    cityIds: zakres === 'CITY' ? odbiorcy.miasta.map((m) => m.id) : [],
    schoolIds: zakres === 'SCHOOL' ? odbiorcy.szkoly.map((s) => s.id) : [],
    userIds: zakres === 'USER' ? odbiorcy.uczniowie.map((u) => u.userId) : [],
    startsAt: startIso,
    endsAt: koniecIso,
    noEndConfirmed: !!h?.bezKonca,
    priority: prio,
  } : null), [rewizja, prioOk, adresaciOk, terminOk, zakres, odbiorcy, startIso, koniecIso, h?.bezKonca, prio]);
  const klucz = zadanie ? JSON.stringify(zadanie) : '';

  useEffect(() => {
    if (!zadanie) return;
    let aktualne = true;
    const t = setTimeout(() => {
      tresciApi.kontrolaZastosowania(api, zadanie)
        .then((w) => {
          if (!aktualne) return;
          setKontrola({ klucz, konflikty: w?.konflikty ?? [], nakladania: w?.nakladania ?? [] });
          setBladKontroli(w?.blad && !w.konflikt ? w.blad : null);
        })
        .catch((e: unknown) => { if (aktualne) setBladKontroli(komunikatBledu(e)); });
    }, 400);
    // Starsza odpowiedź (wolniejsza sieć) nie może nadpisać wyniku dla nowszego terminu.
    return () => { aktualne = false; clearTimeout(t); };
  }, [api, zadanie, klucz]);

  const aktualnaKontrola = kontrola && kontrola.klucz === klucz ? kontrola : null;
  const konflikty = konfliktyZapisu ?? aktualnaKontrola?.konflikty ?? [];
  const nakladania = aktualnaKontrola?.nakladania ?? [];
  const mozna = !!zadanie && !!aktualnaKontrola && konflikty.length === 0 && !trwa;

  const zapisz = async () => {
    if (!zadanie) return;
    setTrwa(true);
    setBlad(null);
    try {
      const w = await tresciApi.zastosujSkorke(api, zadanie);
      const zs = w?.zastosowanie;
      onZaplanowano(`Zaplanowano: ${p.nazwa} · ${zs ? opisAdresatow(zs, 4) : ETYKIETA_ZAKRESU[zakres]} · `
        + `${oknoWarszawa(zs?.startsAt ?? startIso, zs?.endsAt ?? koniecIso)} · priorytet ${prio}`
        + `${typeof zs?.potentialRecipients === 'number' ? ` · ${zs.potentialRecipients.toLocaleString('pl-PL')} potencjalnych odbiorców` : ''}.`);
    } catch (e) {
      const k = konfliktyZBledu(e);
      if (k) setKonfliktyZapisu(k);
      else setBlad(komunikatBledu(e) + (wynikNieznany(e) ? DOPISEK_WYNIK_NIEZNANY : ''));
      if (wynikNieznany(e)) onWynikNieznany();
    } finally {
      setTrwa(false);
    }
  };

  // Zamiana możliwa, gdy każdy konflikt to zastosowanie (publikacji globalnej V1 nie zastępujemy z tego okna).
  const doZamiany = konflikty.length > 0 && konflikty.every((k) => !k.legacy && typeof k.assignmentId === 'number')
    ? konflikty.map((k) => k.assignmentId as number) : null;
  const mozeZamienic = !!zadanie && !!doZamiany && auth.has('app.content.publish') && !trwa;

  const zamien = async () => {
    if (!zadanie || !doZamiany) return;
    setTrwa(true);
    setBlad(null);
    setUwagaZamiany(null);
    try {
      const w = await tresciApi.zamienZastosowanie(api, { ...zadanie, replaceAssignmentIds: doZamiany });
      const zs = w?.zastosowanie;
      const ile = w?.zastapione?.length ?? doZamiany.length;
      setZamiana(false);
      onZaplanowano(`Zamieniono: ${p.nazwa} zastąpiła ${ile === 1 ? '1 zastosowanie' : `${ile} zastosowania`} · `
        + `${zs ? opisAdresatow(zs, 4) : ETYKIETA_ZAKRESU[zakres]} · ${oknoWarszawa(zs?.startsAt ?? startIso, zs?.endsAt ?? koniecIso)} · priorytet ${prio}.`);
    } catch (e) {
      const k = konfliktyZBledu(e);
      setZamiana(false);
      if (k) {
        // Ktoś zmienił zastosowania w międzyczasie — nic nie zmieniono, operator potwierdza świeżą listę.
        setKonfliktyZapisu(k);
        setUwagaZamiany('Kolizje zmieniły się od sprawdzenia — nic nie zmieniono. Sprawdź aktualną listę i potwierdź zamianę ponownie.');
      } else {
        setBlad(komunikatBledu(e) + (wynikNieznany(e) ? DOPISEK_WYNIK_NIEZNANY : ''));
      }
      if (wynikNieznany(e)) onWynikNieznany();
    } finally {
      setTrwa(false);
    }
  };

  const wrocDoTerminu = () => {
    setKonfliktyZapisu(null);
    document.getElementById('termin-skorki')?.scrollIntoView?.({ block: 'center' });
  };

  return (
    <Dialog open onClose={onZamknij} maxWidth="md" fullWidth aria-labelledby="zaplanuj-skorke-tytul">
      <DialogTitle id="zaplanuj-skorke-tytul">Zaplanuj skórkę: {p.nazwa}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">{z.etykieta}{z.ostrzezenie ? ` — ${z.ostrzezenie}` : ''}</Typography>
          {!rewizja ? (
            <Alert severity="warning" role="alert">Ta skórka nie ma rewizji gotowej do użycia (wyłączona albo z błędami) — dodaj nową wersję.</Alert>
          ) : null}

          <Box component="section" aria-labelledby="sekcja-gdzie">
            <Typography id="sekcja-gdzie" variant="subtitle2" sx={{ mb: 1 }}>A. Gdzie ma się wyświetlać?</Typography>
            <ToggleButtonGroup exclusive size="small" value={zakres} aria-label="Gdzie ma się wyświetlać"
              onChange={(_, v: ZakresPaska | null) => { if (v) { setZakres(v); setOdbiorcy(PUSCI_ODBIORCY); setKonfliktyZapisu(null); } }}
              sx={{ flexWrap: 'wrap', mb: 1 }}>
              {(Object.keys(ETYKIETA_ZAKRESU) as ZakresPaska[]).map((k) => (
                <ToggleButton key={k} value={k} aria-label={ETYKIETA_ZAKRESU[k]}>{ETYKIETA_ZAKRESU[k]}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid="opis-zakresu">{OPIS_ZAKRESU[zakres]}</Typography>
            <WyborOdbiorcow pelneDaneOsob zakres={zakres} wartosc={odbiorcy} onZmiana={(w) => { setOdbiorcy(w); setKonfliktyZapisu(null); }} />
          </Box>

          <Box component="section" aria-labelledby="sekcja-kiedy" id="termin-skorki">
            <Typography id="sekcja-kiedy" variant="subtitle2" sx={{ mb: 1 }}>B. Kiedy? (czas warszawski)</Typography>
            <HarmonogramOdDo onZmiana={onH} etykietaGrupy="Termin skórki" />
          </Box>

          <TextField label="Priorytet (0–100)" size="small" sx={{ maxWidth: 220 }} value={priorytet}
            onChange={(e) => setPriorytet(e.target.value.trim())} error={!prioOk}
            helperText="Rozstrzyga tylko w tym samym zakresie. Węższy zakres wygrywa zawsze: osoby > szkoła > miasto > wszyscy." />

          {uwagaZamiany ? <Alert severity="info" role="status" data-testid="uwaga-zamiany">{uwagaZamiany}</Alert> : null}
          {konflikty.length ? (
            <Alert severity="warning" role="alert" data-testid="kolizja"
              action={(
                <Stack spacing={1} sx={{ alignItems: 'stretch' }}>
                  <Button size="small" onClick={wrocDoTerminu}>Wróć i zmień termin</Button>
                  {doZamiany ? (
                    <Button size="small" variant="contained" color="warning" disabled={!mozeZamienic} onClick={() => setZamiana(true)}>
                      Zamień na tę skórkę
                    </Button>
                  ) : null}
                </Stack>
              )}>
              Ten termin nakłada się na:
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {konflikty.map((k) => <li key={`${k.assignmentId ?? 'v1'}-${k.revisionId}`}>{opisKolizjiZastosowania(k)}</li>)}
              </ul>
              Ci sami adresaci i ten sam priorytet — wynik byłby niejednoznaczny. Zmień termin, adresatów albo priorytet
              {doZamiany ? ' — albo zamień obecną skórkę na tę' : ''}. Nic nie zostało zapisane.
              {!doZamiany ? (
                <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
                  Publikacji globalnej V1 nie da się zastąpić z tego okna — wyłącz ją w „Szczegóły techniczne”.
                </Typography>
              ) : null}
            </Alert>
          ) : null}
          {nakladania.length && !konflikty.length ? (
            <Alert severity="info" role="status" data-testid="nakladania">
              Nakłada się z innymi zastosowaniami — pierwszeństwo jest jednoznaczne:
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {nakladania.map((k) => <li key={`${k.assignmentId ?? 'v1'}-${k.revisionId}`}>{opisKolizjiZastosowania(k)} — {k.outcome}</li>)}
              </ul>
            </Alert>
          ) : null}
          {zadanie && !aktualnaKontrola && !bladKontroli ? <Typography variant="caption" role="status">Sprawdzam kolizje…</Typography> : null}
          {zadanie && aktualnaKontrola && !konflikty.length ? (
            <Typography variant="caption" sx={{ color: 'success.main' }} data-testid="bez-konfliktow">Bez konfliktów dla tych adresatów i terminu.</Typography>
          ) : null}
          {bladKontroli ? <Alert severity="error" role="alert">Kontrola kolizji: {bladKontroli}</Alert> : null}
          {blad ? <Alert severity="error" role="alert">{blad}</Alert> : null}
          {!adresaciOk ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>Wybierz co najmniej jednego adresata.</Typography> : null}
          {h && !h.bezKonca && !h.koniec ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Ustaw datę DO albo świadomie zaznacz „bez daty końcowej".</Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onZamknij}>Anuluj</Button>
        <Button variant="contained" disabled={!mozna} onClick={() => { void zapisz(); }}>
          {trwa && !zamiana ? 'Zapisuję…' : 'Potwierdź i zaplanuj'}
        </Button>
      </DialogActions>
      {zamiana && zadanie && doZamiany ? (
        <PotwierdzZamiane nowa={p.nazwa} konflikty={konflikty} zakres={zakres} odbiorcy={odbiorcy}
          okno={oknoWarszawa(startIso, koniecIso)} priorytet={prio} trwa={trwa}
          onAnuluj={() => setZamiana(false)} onZamien={() => { void zamien(); }} />
      ) : null}
    </Dialog>
  );
}
